import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core'
import { environment } from '../environments/environment'
import { Title } from '@angular/platform-browser'
import { appConfig } from '../brands/_current/config/app-config'
import { parse } from 'semver'
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router'
import { filter, switchMap, take, tap } from 'rxjs/operators'
import { CommonHeaderService } from './shared/common-headers/common-header.service'
import { convertLocaleToCultureHeader } from './shared/common-headers/culture-header'
import { ScriptLoaderService } from '@mycicero/ng-script-loader'
import { Angulartics2GoogleGlobalSiteTag } from 'angulartics2'
import { AvacyCookieBannerService, FACEBOOK_VENDOR_ID } from './shared/cookie-banner/avacy-cookie-banner.service'
import { AnalyticsService } from './shared/analytics/analytics.service'
import { MessageService } from './shared/toast-module/messageservice'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly title: Title,
    private readonly commonHeaders: CommonHeaderService,
    private readonly analyticsService: AnalyticsService,
    private readonly scriptLoaderService: ScriptLoaderService,
    private readonly angulartics2GoogleGlobalSiteTag: Angulartics2GoogleGlobalSiteTag,
    private readonly avacyService: AvacyCookieBannerService,
    private readonly messageService: MessageService,
    @Inject(LOCALE_ID) private readonly lang: string,
  ) {}

  private static reduceDataListToPageTitle(dataList: Data[]): string {
    return dataList
      .reduce(
        (acc: string[], curr: Data) => {
          if (curr['title']) {
            return [...acc, curr['title']]
          }
          return acc
        },
        [appConfig.core.documentTitle],
      )
      .reverse()
      .join(' - ')
  }

  private static flattenActivatedRouteToDataList(route: ActivatedRoute): Data[] {
    const acc = [route.data]
    let iterator = route
    while (iterator.firstChild) {
      acc.push(iterator.firstChild.data)
      iterator = iterator.firstChild
    }
    return acc
  }

  ngOnInit(): void {
    const version = parse(environment.version)
    const client = !version
      ? appConfig.core.clientName
      : `${appConfig.core.clientName};${version.major}.${version.minor}.${version.patch}`

    this.commonHeaders.set('Culture', convertLocaleToCultureHeader(this.lang))
    this.commonHeaders.set('Client', client)
    const clientId = this.getClientIdFromReturnUrl()
    if (clientId) {
      this.commonHeaders.set('AuthorizeClientId', clientId)
    }

    if (appConfig.cookieBanner.avacyCookieBanner && this.analyticsService.forcedAnalyticsConsent === null) {
      this.avacyService.initAvacyCookieBanner()
    }

    this.initMetaPixel()
    this.initGoogleGlobalSiteTag()

    this.router.events
      .pipe(
        tap((event) => {
          if (event instanceof NavigationEnd) {
            const dataList = AppComponent.flattenActivatedRouteToDataList(this.route)
            const title = AppComponent.reduceDataListToPageTitle(dataList)
            this.title.setTitle(title)
          }
        }),
      )
      .subscribe()
  }

  private getClientIdFromReturnUrl(): string | null {
    try {
      const currentUrl = new URL(window.location.href)
      const returnUrl = currentUrl.searchParams.get('returnUrl')

      if (!returnUrl) {
        return null
      }

      const decodedReturnUrl = decodeURIComponent(returnUrl)
      const parsedReturnUrl = new URL(decodedReturnUrl, window.location.origin)

      return parsedReturnUrl.searchParams.get('client_id')
    } catch {
      return null
    }
  }

  private initGoogleGlobalSiteTag(): void {
    if (
      appConfig.analytics.googleGlobalSiteTag.enabled &&
      appConfig.analytics.googleGlobalSiteTag.trackers.length &&
      this.analyticsService.forcedAnalyticsConsent !== false
    ) {
      this.scriptLoaderService
        .loadScript('googleGlobalSiteTag1', {
          innerHTML: `window['gtag_enable_tcf_support'] = true;`,
          targetEl: 'head',
        })
        .subscribe()

      /**
       * MWEB-2099: "su Google Tag Manager ci sono ancora gli attributi data-managed, data-purposes e gli altri.
       * Avendo abilitato il supporto al TCF, gli attributi possono essere rimossi e lasciare lo script originale."
       */
      this.scriptLoaderService
        .loadScript('googleGlobalSiteTag2', {
          url: `https://www.googletagmanager.com/gtag/js?id=${appConfig.analytics.googleGlobalSiteTag.trackers[0].id}`,
          attributes: {
            async: 'true',
          },
          targetEl: 'head',
        })
        .subscribe()

      this.scriptLoaderService
        .loadScript('googleGlobalSiteTag3', {
          innerHTML: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date());`,
          targetEl: 'head',
        })
        .subscribe(() => this.angulartics2GoogleGlobalSiteTag.startTracking())
    }
  }

  private initMetaPixel(): void {
    if (appConfig.analytics.metaPixel.enabled && this.analyticsService.forcedAnalyticsConsent !== false) {
      const innerHTML = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${appConfig.analytics.metaPixel.id}');
fbq('track', 'PageView');
`

      if (!appConfig.cookieBanner.avacyCookieBanner || this.analyticsService.forcedAnalyticsConsent === true) {
        this.scriptLoaderService
          .loadScript('metaPixel', {
            innerHTML,
            attributes: { type: 'text/javascript' },
            targetEl: 'head',
          })
          .subscribe()
      } else if (this.analyticsService.forcedAnalyticsConsent === null) {
        this.avacyService.avacyPreferences$
          .pipe(
            filter((avacyPreferences) => !!avacyPreferences || !appConfig.cookieBanner.avacyCookieBanner),
            take(1),
            switchMap((avacyPreferences) =>
              this.scriptLoaderService.loadScript('metaPixel', {
                innerHTML,
                attributes: !avacyPreferences?.customVendorIds.includes(FACEBOOK_VENDOR_ID)
                  ? {
                      'data-managed': 'as-oil',
                      'data-type': 'text/javascript',
                      'data-purposes': '1,2,3,4,7,9',
                      'data-custom-vendor': FACEBOOK_VENDOR_ID,
                      type: 'as-oil',
                    }
                  : { type: 'text/javascript' },
                targetEl: 'head',
              }),
            ),
          )
          .subscribe()
      }
    }
  }
}
